import React, { useMemo, useState } from "react";
import AgentLayout from "../components/AgentLayout";
import NavButton from "../components/NavButton";
import DatePickerComponent from "../../client/components/DatePickerComponent";
import EasyGrid from "../../client/components/EasyGrid";
import axiosInstance from "../../../utils/axiosInstance";
import { useSelector } from "react-redux";
import Bootstrap4DatePickerComponent from "../../client/components/Bootstrap4DatePickerComponent";
import "datatables.net-bs4/js/dataTables.bootstrap4.js";
import "datatables.net-bs4/css/dataTables.bootstrap4.css";
import "datatables.net-responsive-bs4/js/responsive.bootstrap4.js";
import "datatables.net-responsive-bs4/css/responsive.bootstrap4.css";

function toLocalString(d) {
  const pad = (x) => String(x).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

const GAME_TYPE_LABELS = {
  cricket: "Cricket",
  fancy: "Fancy",
  soccer: "Soccer",
  tennis: "Tennis",
  greyhound: "Greyhound",
  horseRace: "Horse Race",
  tpStudio: "Teenpatti Studio",
  galexyStudio: "Galaxy Casino",
  betfairGames: "Betfair Games",
  worldCasino: "World Casino"
};

function escapeHtml(str = "") {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const formatTruncate = (value) => {
  const num = Math.round(value);
  const truncated = Math.floor(num * 1000) / 1000;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  }).format(truncated);
};

const DailyPlByGameType = () => {
  const today = new Date();
  const year = today.getFullYear();
  const monthIndex = today.getMonth();
  const dayOfMonth = today.getDate();
  const todayStart = new Date(year, monthIndex, dayOfMonth, 0, 0, 0, 0);
  const todayEnd = new Date(year, monthIndex, dayOfMonth, 23, 59, 59, 999);

  const [startDate, setStartDate] = useState(todayStart);
  const [endDate, setEndDate] = useState(todayEnd);
  const [showLoader, setShowLoader] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize] = useState(10);

  const [profitGameTypes, setProfitGameTypes] = useState([]);
  const [lossGameTypes, setLossGameTypes] = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalLoss, setTotalLoss] = useState(0);

  const [selectedGameType, setSelectedGameType] = useState("");
  const [selectedGameTypeLabel, setSelectedGameTypeLabel] = useState("");
  const [gamesDisplay, setGamesDisplay] = useState(false);
  const [gamesData, setGamesData] = useState([]);

  const [selectedGameTitle, setSelectedGameTitle] = useState("");
  const [transactionsData, setTransactionsData] = useState([]);

  const userInfo = useSelector((state) => state.userStore);
  let username = "";
  if (userInfo?.data?.uname) {
    username = userInfo.data.uname;
  }

  const startDateSet = (date) => {
    setStartDate(date);
  };

  const endDateSet = (date) => {
    setEndDate(date);
  };

  const splitGameTypes = (data) => {
    const profit = [];
    const loss = [];
    let profitTotal = 0;
    let lossTotal = 0;

    Object.entries(data || {}).forEach(([key, amount]) => {
      if (key === "grandTotal") return;
      const numAmount = Number(amount) || 0;
      if (numAmount === 0) return;
      const row = {
        _id: key,
        market_name: GAME_TYPE_LABELS[key] || key,
        winAmount: numAmount,
        game_type: key
      };
      if (row.winAmount >= 0) {
        profit.push(row);
        profitTotal += row.winAmount;
      } else {
        loss.push(row);
        lossTotal += row.winAmount;
      }
    });

    setProfitGameTypes(profit);
    setLossGameTypes(loss);
    setTotalProfit(profitTotal);
    setTotalLoss(lossTotal);
  };

  const getDatePayload = () => ({
    frm: toLocalString(startDate),
    til: toLocalString(endDate)
  });

  const loadGameTypes = async () => {
    setShowLoader(true);
    setGamesDisplay(false);
    setGamesData([]);
    setTransactionsData([]);
    setSelectedGameType("");
    setSelectedGameTypeLabel("");
    setSelectedGameTitle("");

    try {
      const response = await axiosInstance.post(
        "/master/daily-pl-by-game-type",
        getDatePayload()
      );
      if (response.status === 200) {
        splitGameTypes(response.data.data);
        setDataLoaded(true);
        setIsLoading(false);
      }
    } finally {
      setShowLoader(false);
    }
  };

  const handleGameTypeClick = async (row) => {
    setShowLoader(true);
    setSelectedGameType(row.game_type);
    setSelectedGameTypeLabel(row.market_name);
    setTransactionsData([]);
    setSelectedGameTitle("");

    try {
      const response = await axiosInstance.post(
        "/master/daily-pl-by-game-type",
        {
          ...getDatePayload(),
          game_type: row.game_type
        }
      );
      if (response.status === 200) {
        setGamesData(
          (response.data.data || []).map((item, idx) => ({
            ...item,
            market_name: item.game_name,
            amount: item.profit_loss,
            _id: item.reference_id ?? `game_${idx}`
          }))
        );
        setGamesDisplay(true);
      }
    } finally {
      setShowLoader(false);
    }
  };

  const handleGameClick = async (row) => {
    setShowLoader(true);
    setSelectedGameTitle(row.game_name || row.market_name || "");

    try {
      const response = await axiosInstance.post(
        "/master/daily-pl-by-game-type",
        {
          ...getDatePayload(),
          game_type: selectedGameType,
          market_type: row.market_type,
          reference_id: row.reference_id
        }
      );
      if (response.status === 200) {
        setTransactionsData(
          (response.data.data || []).map((item, idx) => ({
            ...item,
            _id: item.id ?? item._id ?? `tx_${idx}`
          }))
        );
      }
    } finally {
      setShowLoader(false);
    }
  };

  const openPopUp = (id) => {
    const popup = window.open(
      `/plby-hierarchical/${id}`,
      "",
      "width=700,height=500"
    );
    if (popup) popup.document.title = "BPEXCH";
  };

  const betDetailsView = (id) => {
    const popup = window.open(
      `/bet-details-view/${id}`,
      "",
      "width=700,height=500"
    );
    if (popup) popup.document.title = "BPEXCH";
  };

  const betDetailsVirtualView = (id) => {
    const popup = window.open(
      `/virtual-details-view/${id}`,
      "",
      "width=700,height=500"
    );
    if (popup) popup.document.title = "BPEXCH";
  };

  const handleTransactionClick = (row) => {
    if (row.user_role == 8) {
      if (row.game_type == 9) {
        betDetailsVirtualView(row.id);
      } else {
        betDetailsView(row.id);
      }
    } else {
      openPopUp(row.id);
    }
  };

  const gameTypeCols = useMemo(
    () => [
      {
        title: "Game Type",
        data: "market_name",
        responsivePriority: 1,
        render: function (d, type, row) {
          const safe = escapeHtml(String(d ?? ""));
          const key = escapeHtml(String(row._dt_key ?? ""));
          return `<a href="#" class="dt-user-link" data-row-key="${key}" style="color:#00b181;cursor:pointer;padding-left:4px">${safe}</a>`;
        }
      },
      {
        title: "Amount",
        data: "winAmount",
        render: (d) =>
          d < 0
            ? `<span style="color:red">${formatTruncate(Math.round(d))}</span>`
            : `<span style="color:#00b181">${formatTruncate(
                Math.round(d)
              )}</span>`,
        responsivePriority: 2
      },
      {
        title: "",
        data: "game_type",
        visible: false,
        responsivePriority: 3
      }
    ],
    []
  );

  const gameCols = useMemo(
    () => [
      {
        title: "Game",
        data: "market_name",
        responsivePriority: 1,
        render: function (d, type, row) {
          const safe = escapeHtml(String(d ?? ""));
          const key = escapeHtml(String(row._dt_key ?? ""));
          return `<a href="#" class="dt-user-link" data-row-key="${key}" style="color:#00b181;cursor:pointer;padding-left:4px">${safe}</a>`;
        }
      },
      {
        title: "Amount",
        data: "amount",
        render: (d) =>
          d < 0
            ? `<span style="color:red">${formatTruncate(Math.round(d))}</span>`
            : `<span style="color:#00b181">${formatTruncate(
                Math.round(d)
              )}</span>`,
        responsivePriority: 2
      },
      {
        title: "",
        data: "reference_id",
        visible: false,
        responsivePriority: 3
      }
    ],
    []
  );

  const transactionCols = useMemo(
    () => [
      {
        title: "Date",
        data: "stmp",
        render: (d) => `<span>${d}</span>`,
        responsivePriority: 2
      },
      {
        title: "Event",
        data: "match",
        render: function (d, type, row) {
          const safe = escapeHtml(String(d ?? ""));
          const key = escapeHtml(String(row._dt_key ?? ""));
          return `<a href="#" class="dt-user-link" data-row-key="${key}" style="color:#00b181;cursor:pointer;padding-left:4px">${safe}</a>`;
        },
        responsivePriority: 2
      },
      {
        title: "",
        data: "id",
        visible: false,
        responsivePriority: 3
      },
      {
        title: "Amount",
        data: "win_amt",
        responsivePriority: 1,
        render: function (d, type, row) {
          const safe = Number(d ?? 0);
          const key = escapeHtml(String(row._dt_key ?? ""));
          return safe < 0
            ? `<a href="#" class="dt-user-link" data-row-key="${key}" style="color:red;cursor:pointer">${formatTruncate(
                safe
              )}</a>`
            : `<a href="#" class="dt-user-link" data-row-key="${key}" style="color:#00b181;cursor:pointer">${formatTruncate(
                safe
              )}</a>`;
        }
      }
    ],
    []
  );

  return (
    <AgentLayout>
      <style>{`.react-datepicker-popper {
             left: unset;
        }
        @media (max-width: 640px) {
          #ReportFilterForm .row > .col-12.col-md-5 {
            flex: 0 0 100%;
            max-width: 100%;
            margin-bottom: 8px;
          }
          #ReportFilterForm .row > .col-12.col-md-5 .react-datepicker-wrapper,
          #ReportFilterForm .row > .col-12.col-md-5 .react-datepicker__input-container,
          #ReportFilterForm .row > .col-12.col-md-5 .form-group,
          #ReportFilterForm .row > .col-12.col-md-5 .input-group {
            width: 100% !important;
          }
          #ReportFilterForm .row > .col-12.col-md-5 .input-group .form-control {
            flex: 1;
          }
          #ReportFilterForm .row > .col-12.col-md-5 .input-group-addon {
            display: flex;
            align-items: center;
          }
          #ReportFilterForm .input-group {
            flex-wrap: nowrap !important;
          }
          #ReportFilterForm .input-group .input-group-addon {
            display: flex;
            align-items: stretch;
          }
          #ReportFilterForm .input-group .input-group-addon .input-group-text {
            display: flex;
            align-items: center;
            align-self: stretch;
            height: auto;
            padding: 0 0.75rem;
            background-color: #e9ecef;
            border: 1px solid #ced4da;
            border-left: none;
            border-radius: 0 0.25rem 0.25rem 0;
          }
          #ReportFilterForm .input-group .form-control {
            border-radius: 0.25rem 0 0 0.25rem;
          }
          #ReportFilterForm .row > strong {
            display: none;
          }
          #ReportFilterForm .row > .editsbmtbtn {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            padding-right: 20px;
          }
        }`}</style>
      <div className="container-fluid">
        <div
          className="loaderAgent"
          style={{ display: showLoader ? "" : "none" }}
        >
          <img src="/assets/images/loadinggif.gif" alt="" />
        </div>

        <div className="bets-section-wrapper">
          <div>
            <NavButton />
          </div>

          <div>
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <i className="fa fa-filter"></i>
                    <strong>Report Filter</strong>
                  </div>
                  <div
                    className="card-body"
                    style={{ padding: "0.8rem 1.25rem" }}
                  >
                    <form
                      id="ReportFilterForm"
                      className="form-inline"
                      method="get"
                    >
                      <div className="row" style={{ textAlignLast: "justify" }}>
                        <div className="col-12 col-md-5">
                          <Bootstrap4DatePickerComponent
                            pickerId="ReportFrom"
                            inputId="DisplayFrom"
                            defaultDate={startDate}
                            setSelectedDate={startDateSet}
                          />
                          {/*<DatePickerComponent
                            defaultDate={startDate}
                            setSelectedDate={setStartDate}
      />*/}
                        </div>
                        <strong style={{ margin: "auto" }}>
                          &nbsp;-&nbsp;
                        </strong>
                        <div className="col-12 col-md-5">
                          <Bootstrap4DatePickerComponent
                            pickerId="ReportTo"
                            inputId="DisplayTo"
                            defaultDate={endDate}
                            setSelectedDate={endDateSet}
                          />
                          {/*<DatePickerComponent
                            defaultDate={endDate}
                            setSelectedDate={setEndDate}
    />*/}
                        </div>
                        <div className="form-group editsbmtbtn">
                          <button
                            onClick={() => loadGameTypes()}
                            className="btn btn-success"
                            style={{ background: "#02b181" }}
                            type="button"
                          >
                            <strong>Submit</strong>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="bets-wrapper"
          style={{ marginTop: "20px", display: dataLoaded ? "" : "none" }}
        >
          <div className="row">
            <div className="col-md-6" style={{ marginTop: "3px" }}>
              <div className="card">
                <div className="card-header">
                  <div style={{ float: "left" }}>
                    <i className="fa fa-align-justify"></i>
                    <span style={{ fontWeight: "600" }}> {username}</span>
                    <br />
                    Game Type Report
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-6">
                      <EasyGrid
                        responsiveMode="hybrid"
                        cols={gameTypeCols}
                        dataset={profitGameTypes}
                        isLoading={isLoading}
                        pageSize={pageSize}
                        handleUserClick={handleGameTypeClick}
                      />
                    </div>
                    <div className="col-6">
                      <EasyGrid
                        responsiveMode="hybrid"
                        cols={gameTypeCols}
                        dataset={lossGameTypes}
                        isLoading={isLoading}
                        pageSize={pageSize}
                        handleUserClick={handleGameTypeClick}
                      />
                    </div>

                    <div className="col-6">
                      <table className="table table-sm dataTable">
                        <tbody>
                          <tr className="bg-primary" style={{ color: "white" }}>
                            <td>Total</td>
                            <td>{formatTruncate(Math.round(totalProfit))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col-6">
                      <table className="table table-sm">
                        <tbody>
                          <tr className="bg-danger" style={{ color: "white" }}>
                            <td>Total</td>
                            <td>{formatTruncate(Math.round(totalLoss))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="col-md-6"
              style={{ marginTop: "3px", display: gamesDisplay ? "" : "none" }}
            >
              <div>
                <div className="card">
                  <div className="card-header">
                    <div style={{ float: "left" }}>
                      <i className="fa fa-align-justify"></i>
                      <span style={{ fontWeight: "bold", paddingLeft: "10px" }}>
                        {selectedGameTypeLabel}
                      </span>
                      - Games Report
                    </div>
                  </div>
                  <div className="card-body">
                    <EasyGrid
                      responsiveMode="hybrid"
                      cols={gameCols}
                      dataset={gamesData}
                      isLoading={isLoading}
                      pageSize={pageSize}
                      handleUserClick={handleGameClick}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "20px" }}>
                {transactionsData.length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <div style={{ float: "left" }}>
                        <i className="fa fa-align-justify"></i>
                        <span
                          style={{ fontWeight: "bold", paddingLeft: "10px" }}
                        >
                          {selectedGameTypeLabel}
                        </span>
                        {selectedGameTitle ? ` - ${selectedGameTitle}` : ""}
                      </div>
                    </div>
                    <div className="card-body">
                      <EasyGrid
                        responsiveMode="hybrid"
                        cols={transactionCols}
                        dataset={transactionsData}
                        isLoading={isLoading}
                        pageSize={pageSize}
                        handleUserClick={handleTransactionClick}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <style>{`
.loaderAgent {
  position: fixed;
  z-index: 99;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  opacity: .6;
  display: flex;
  justify-content: center;
  align-items: center;
}
`}</style>
      </div>
    </AgentLayout>
  );
};

export default DailyPlByGameType;
